import Hls, {ManifestParsedData, ErrorData} from 'hls.js';

/**
 * hls.js
 * https://github.com/video-dev/hls.js/
 */

declare global {
  interface Window {
    hls: Hls;
    nextHls: Hls | undefined;
    hlsVersion: string;
  }
}
interface IHlsSourceProps {
  hlsUrl: string;
  videoRef: HTMLVideoElement;
  startPosition: number;
  handleHlsEvent: {
    onManifestParsed: (
      event: 'hlsManifestParsed',
      data: ManifestParsedData,
    ) => void;
  };
  isPrepareNextFile?: boolean;
  isSubscriptionVod?: boolean;
}

const HlsContainer = ({
  hlsUrl,
  videoRef,
  startPosition,
  handleHlsEvent,
  isPrepareNextFile = false,
  isSubscriptionVod,
}: IHlsSourceProps) => {
  // 이미 hls 객체 존재하고, 다음 파일 미리 로드하는 로직 아닐 경우
  // 다른 파일로 seeking 할 때만 destroy
  if (window.hls && !isPrepareNextFile) {
    window.hls.destroy();
  }

  const retryCnt = 3;
  const hlsConfig = {
    debug: false,
    nudgeOffset: 0.3, //default 0.1s 였는데 BUFFER_NUDGE_ON_STALL 발생했을 때 currentTime을 더 많이 움직여 더빨리 찾도록 0.5로 늘림
    highBufferWatchdogPeriod: 1, //default 3s 였는데 BUFFER_NUDGE_ON_STALL 발생했을 때 너무 오래걸려서 1s로 줄임
    startPosition: startPosition,
    manifestLoadPolicy: {
      default: {
        maxTimeToFirstByteMs: Infinity,
        maxLoadTimeMs: 10000,
        timeoutRetry: {
          maxNumRetry: 0,
          retryDelayMs: 0,
          maxRetryDelayMs: 0,
        },
        errorRetry: {
          maxNumRetry: retryCnt,
          retryDelayMs: 1000,
          maxRetryDelayMs: 8000,
        },
      },
    },
    playlistLoadPolicy: {
      default: {
        maxTimeToFirstByteMs: 15000,
        maxLoadTimeMs: 15000,
        timeoutRetry: {
          maxNumRetry: 0,
          retryDelayMs: 0,
          maxRetryDelayMs: 0,
        },
        errorRetry: {
          maxNumRetry: retryCnt,
          retryDelayMs: 1000,
          maxRetryDelayMs: 8000,
        }, // parent m3u8의 화질들 전부 요청해보고, 마지막 요청값의 m3u8을 maxNumRetry만큼 재시도함.
      },
    },
    fragLoadPolicy: {
      default: {
        maxTimeToFirstByteMs: 20000,
        maxLoadTimeMs: 20000,
        timeoutRetry: {
          maxNumRetry: 3,
          retryDelayMs: 1000,
          maxRetryDelayMs: 64000,
        },
        errorRetry: {
          maxNumRetry: retryCnt,
          retryDelayMs: 10000,
          maxRetryDelayMs: 64000,
        }, // 4xx 상태가 반환되면 조각을 다시 요청해도 결과가 변경될 것으로 예상되지 않으므로 플레이어가 다음 조각으로 이동함.
      },
    },
  };

  const hlsObject = new Hls(hlsConfig);

  window.hlsVersion = Hls.version || '';

  hlsObject.config.xhrSetup = (xhr, url) => {
    if (isSubscriptionVod) {
      xhr.withCredentials = true; // do send signed cookies
    }

    // vod.afreecatv.com에서 veditor.afreecatv.com로 같은 영상에 대해 넘어오면 브라우저 캐시로 cors에러가 발생하여 m3u8과 ts 뒤에 쿼리스트링 넣어줌
    xhr.open('GET', url + '?type=veditor', true);
    // xhr.withCredentials = true;
    // console.log("xhr: ", xhr);
    // xhr.setRequestHeader("Pragma", "no-cache");
    // xhr.setRequestHeader("Cache-Control", "no-cache");
    // xhr.setRequestHeader("Vary", "Origin");
    // xhr.send("md5=test&expire=test123");
  };

  // hlsObject.config.fetchSetup = (context, initParams) => {
  //     // initParams.mode = "no-cors";
  //     // initParams.credentials = "include";
  //     return new Request(
  //         context.url + "?md5=123123&expires=test123",
  //         initParams,
  //     );
  // };
  // console.log("hlsObject: ", hlsObject);

  const video = videoRef as HTMLVideoElement;

  const onHlsError = (event: 'hlsError', data: ErrorData) => {
    // const errorType = data.type;
    const errorDetails = data.details;
    const errorFatal = data.fatal;

    if (errorDetails) {
      switch (errorDetails) {
        case Hls.ErrorDetails.FRAG_LOAD_ERROR:
          break;
        case Hls.ErrorDetails.MANIFEST_LOAD_ERROR: // 403, 404
        case Hls.ErrorDetails.MANIFEST_PARSING_ERROR:
          break;
        case Hls.ErrorDetails.BUFFER_STALLED_ERROR:
        case Hls.ErrorDetails.BUFFER_NUDGE_ON_STALL:
          console.log('Hls errorDetails: ', errorDetails);
          break;
        default:
          break;
      }
    }

    if (errorFatal) {
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          console.warn('fatal NETWORK error encoutered, try to recover');
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          console.warn('fatal MEDIA error encoutered, try to recover');
          break;
        default:
          console.warn('fatal error, destroy: ');
          console.log(data);
          break;
      }
    }
  };

  if (Hls.isSupported()) {
    hlsObject.loadSource(hlsUrl);
    hlsObject.attachMedia(video);

    hlsObject.on(Hls.Events.MANIFEST_PARSED, handleHlsEvent.onManifestParsed);
    hlsObject.on(Hls.Events.ERROR, onHlsError);

    if (!isPrepareNextFile) {
      window.hls = hlsObject; // typescript는 window 객체 컴파일 오류 발생
    } else {
      window.nextHls = hlsObject;
    }
  }
};

export default HlsContainer;
